import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";

import { countTaskByType } from "features/Influencer/HomePage/Gamification/helpers";
import {
  GamificationProfile,
  OrganizationGamificationTask,
} from "features/Influencer/HomePage/Gamification/schema";

import { fetcherAuth } from "./api";
import { trackEvent } from "./tracking";
import { makeDeepCopy } from "./updateLocalState";

export const useGamification = () => {
  const { setErrorAlert } = useContext(AlertContext);
  const [profileLoading, setProfileLoading] = useState(false);
  const { getToken } = useAuth();
  const {
    currentOrg,
    gamificationProfile,
    setGamificationProfile,
    setShowGamificationProfile,
    setCredits,
  } = useContext(OrganizationUserContext);

  const fetchGamificationProfile = async (refreshEmailStreak?: boolean) => {
    setProfileLoading(true);
    try {
      const { gamificationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/profile`,
      );
      if (!gamificationProfile) {
        setProfileLoading(false);
        return;
      }
      if (gamificationProfile?.completed) {
        setShowGamificationProfile(false);
        return;
      }
      setGamificationProfile(gamificationProfile);
      if (currentOrg?.isProUser) {
        // Already completed streak , no need to fetch past email streaks
        if (
          gamificationProfile &&
          gamificationProfile?.tasksCompleted &&
          gamificationProfile?.tasksCompleted?.includes(
            OrganizationGamificationTask.email_streak,
          )
        ) {
          setGamificationProfile((prev: GamificationProfile | null) => ({
            ...(prev || gamificationProfile),
            emailStreaks: [],
            longestStreak: 4,
          }));
          setProfileLoading(false);
        } else {
          _fetchEmailStreaks(refreshEmailStreak);
        }
      } else {
        setProfileLoading(false);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const refreshGamificationProfile = () => {
    // Only refresh if the profile already existed.
    if (!gamificationProfile) {
      return;
    }
    trackEvent("Gamification - Refetch Profile");
    fetchGamificationProfile(true);
  };

  const addTemplateTaskCompleted = () => {
    if (!gamificationProfile) return;
    if (currentOrg?.isVaUser || currentOrg?.isProUser) {
      const incomplete =
        countTaskByType(
          gamificationProfile,
          OrganizationGamificationTask.niche_templates,
        ) < (currentOrg?.isProUser ? 1 : 3);
      if (!incomplete) return;

      setGamificationProfile((prev: GamificationProfile | null) => {
        if (prev) {
          if (!prev?.tasksCompleted) {
            prev.tasksCompleted = [];
          }
          prev.tasksCompleted?.push(
            OrganizationGamificationTask.niche_templates,
          );
        }
        return prev;
      });

      if (currentOrg?.isVaUser) {
        setCredits((prev) => [
          ...(prev || []),
          {
            transactionType: OrganizationGamificationTask.niche_templates,
          },
        ]);
      }
    }
  };

  const _fetchEmailStreaks = async (refresh?: boolean) => {
    setProfileLoading(true);
    try {
      if (currentOrg?.isProUser) {
        const { emailStreaks, longestStreak } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/gamification/email-streaks?refresh=${Boolean(refresh)}`,
        );
        setGamificationProfile((prev: GamificationProfile | null) => {
          const copy = makeDeepCopy(prev);
          copy.emailStreaks = emailStreaks;
          copy.longestStreak = longestStreak || 0;
          return copy;
        });
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      setProfileLoading(true);
      const { credits } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/credits`,
      );
      setCredits(credits);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setProfileLoading(false);
    }
  };

  return {
    profileLoading,
    refreshGamificationProfile,
    fetchGamificationProfile,
    addTemplateTaskCompleted,
    fetchCredits,
  };
};
